import { AutomateTask, success } from "@iflyrpa/share";
import { AxiosProxyParams, errorResponse } from "../../types";
import { USER_MESSAGE } from "../../utils/errorMessages";
import { Http } from "../../utils/http";
import type { SearchAccountInfoParams } from ".";
import { type SearchAccountInfoResponse, type shipinhaoConfigData } from "./types";

/**
 * 视频号账号数据采集
 *
 * 参考《API接口文档-抖音和视频号数据采集》 2.1:
 * POST /cgi-bin/mmfinderassistant-bin/auth/auth_data
 *
 * auth_data 返回账号基础信息（粉丝数、作品数）。
 * 昨日数据（净增关注/新增播放/喜欢/评论）需调用专门的统计接口，
 * auth_data 本身不返回，因此这些字段降级为 null。
 */
export async function getShipinhaoData(
    _task: AutomateTask,
    params: SearchAccountInfoParams,
): Promise<SearchAccountInfoResponse> {
    try {
        // 验证关键 cookie（参考文档 3.1：视频号只需 sessionid）
        const sessionidCookie = params.cookies.find((it) => it.name === "sessionid")?.value;
        if (!sessionidCookie) {
            return errorResponse("视频号账号数据异常，请重新绑定账号后重试。", 414);
        }

        // 提取必需的请求头信息
        const uinCookie = params.cookies.find((it) => it.name === "uin" || it.name === "wxuin")?.value || "0000000000";
        const deviceIdCookie = params.cookies.find(
            (it) => it.name === "device_id" || it.name === "finger_print_device_id",
        )?.value;
        const aid = params.cookies.find((it) => it.name === "_aid")?.value || "9d3e0e9f-b842-498d-a273-4285d58df264";
        const finderId =
            params.cookies.find((it) => it.name === "_log_finder_id" || it.name === "finder_id")?.value || "";

        const headers = {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            referer: "https://channels.weixin.qq.com/platform",
            origin: "https://channels.weixin.qq.com",
            "content-type": "application/json",
            "X-WECHAT-UIN": uinCookie,
            ...(deviceIdCookie ? { "finger-print-device-id": deviceIdCookie } : {}),
        };

        const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.accountId];

        const http = new Http(...args);

        // 2.1 账号数据接口 auth_data
        const authData = await http.api<{
            errCode?: number;
            errMsg?: string;
            data?: {
                userAttr?: {
                    nickname?: string;
                    encryptedUsername?: string;
                };
                // finderUser 在 data 顶层，包含粉丝数、作品数等核心数据
                finderUser?: {
                    nickname?: string;
                    fansCount?: number; // 粉丝数
                    feedsCount?: number; // 作品总数
                    uniqId?: string; // 视频号ID
                    acctType?: number;
                };
            };
        }>(
            {
                method: "post",
                url: "https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/auth/auth_data",
                params: {
                    _aid: aid,
                    _rid: `${Date.now().toString(16)}-${Math.random().toString(36).substring(2, 9)}`,
                    _pageUrl: encodeURIComponent("https://channels.weixin.qq.com/platform"),
                },
                data: {
                    timestamp: Date.now().toString(),
                    _log_finder_uin: "",
                    _log_finder_id: finderId,
                    rawKeyBuff: "",
                    pluginSessionId: null,
                    scene: 7,
                    reqScene: 7,
                },
            },
            { retries: 3, retryDelay: 100, timeout: 5000 },
        );

        // _task.logger.info(`视频号账号数据响应: ${JSON.stringify(authData)}`);

        // 认证相关的错误码（与 handleShipinhaoData 保持一致）
        const AUTH_ERROR_CODES = [300333, 300334, 300330, 300331, 300332];

        // 检查认证错误：登录失效返回 414，与其他平台一致
        if (authData.errCode && AUTH_ERROR_CODES.includes(authData.errCode)) {
            _task.logger.warn(`视频号登录已失效：${authData.errMsg} (${authData.errCode})`);
            return errorResponse("视频号账号信息获取失败，请检查账号状态", 414);
        }

        // 其他 API 错误
        if (authData.errCode) {
            _task.logger.warn(`视频号账号API错误：${authData.errMsg} (${authData.errCode})`);
            return errorResponse(`视频号账号信息获取失败：${authData.errMsg || "未知错误"}`, 414);
        }

        // finderUser 在 data 顶层，包含粉丝数、作品数
        const finderUser = authData.data?.finderUser;

        // 2.2 获取昨日数据（new_post_total_data 接口）
        const now = new Date();
        const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2); // 与网页一致
        const startTs = Math.floor(yesterday.getTime() / 1000); // 昨日 00:00:00
        const endTs = startTs + 86400;

        _task.logger.info(`视频号昨日数据范围 start: ${startTs} ~ end: ${endTs}`);

        let fansNumYesterday: number = 0;
        let playNumYesterday: number = 0;
        let likeNumYesterday: number = 0;
        let commentNumYesterday: number = 0;

        try {
            const statsData = await http.api<{
                errCode?: number;
                errMsg?: string;
                data?: {
                    totalData?: {
                        browse?: string[];
                        like?: string[];
                        comment?: string[];
                        follow?: string[];
                    };
                };
            }>(
                {
                    method: "post",
                    url: "https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/statistic/new_post_total_data",
                    params: {
                        _aid: aid,
                        _rid: `${Date.now().toString(16)}-${Math.random().toString(36).substring(2, 9)}`,
                        _pageUrl: encodeURIComponent("https://channels.weixin.qq.com/platform"),
                    },
                    data: {
                        startTs: startTs.toString(),
                        endTs: endTs.toString(),
                        interval: 3,
                        timestamp: Date.now().toString(),
                        _log_finder_uin: "",
                        _log_finder_id: finderId,
                        rawKeyBuff: "",
                        pluginSessionId: null,
                        scene: 7,
                        reqScene: 7,
                    },
                },
                { retries: 3, retryDelay: 100, timeout: 5000 },
            );

            // _task.logger.info(`视频号昨日数据响应: ${JSON.stringify(statsData)}`);

            // 不对昨日数据接口的错误做中断处理，降级为 null
            if (!statsData.errCode && statsData.data?.totalData) {
                // 直接从 totalData 提取汇总数据，数组第一个值是昨日数据
                const data = statsData.data.totalData;
                fansNumYesterday = Number.parseInt(data.follow?.[1] || "0", 10) || 0;
                playNumYesterday = Number.parseInt(data.browse?.[1] || "0", 10) || 0;
                likeNumYesterday = Number.parseInt(data.like?.[1] || "0", 10) || 0;
                commentNumYesterday = Number.parseInt(data.comment?.[1] || "0", 10) || 0;

                // _task.logger.info(
                //     `视频号昨日数据提取成功 - 净增关注:${fansNumYesterday}, 播放:${playNumYesterday}, 喜欢:${likeNumYesterday}, 评论:${commentNumYesterday}`,
                // );
            }
        } catch (error) {
            const errMsg = error instanceof Error ? error.message : String(error);
            _task.logger.warn(`视频号昨日数据获取失败（降级为null）: ${errMsg}`);
        }

        const shipinhaoData: shipinhaoConfigData = {
            fansNum: finderUser?.fansCount || 0, // 粉丝数
            feedsCount: finderUser?.feedsCount || 0, // 作品总数（不区分视频/图文）
            fansNumYesterday,
            playNumYesterday,
            likeNumYesterday,
            commentNumYesterday,
        };
        return success(shipinhaoData, "视频号平台数据获取成功！");
    } catch (error) {
        // http 层抛出的是 ResponseData（{code, message, data}），
        // 直接透传以保留代理超时等场景的错误码与友好提示（如 599 / 414）
        if (error && typeof error === "object" && "code" in error && "message" in error) {
            const err = error as { code: number; message: string; data: unknown };
            _task.logger.error(`视频号账号数据获取失败: ${err.message} (${err.code})`);
            return errorResponse(err.message, err.code);
        }

        const errMsg = error instanceof Error ? error.message : String(error);
        _task.logger.error(`视频号账号数据获取失败: ${errMsg}`);
        return errorResponse(USER_MESSAGE.SHIPINHAO_ACCOUNT_FETCH_FAILED);
    }
}
